
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/services/profileService";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { LogOut, User, Settings, FileText, Shield, Info } from "lucide-react";

interface HeaderProps {
  title: string;
  showBack?: boolean;
  action?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ title, showBack = false, action }) => {
  const { user, signOut } = useAuth();
  const { data: profile } = useProfile();
  
  const handleSignOut = async () => {
    await signOut();
  };

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  return (
    <header className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
      <div className="mobile-container">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            {showBack && (
              <Link to="/" className="mr-2 text-gray-500 dark:text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
            )}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center overflow-hidden bg-transparent rounded-full">
                <picture>
                  <source 
                    srcSet="/lovable-uploads/4a75c9be-a35f-43a4-99d5-e02066cae771.webp" 
                    type="image/webp"
                    sizes="40px"
                  />
                  <img 
                    src="/lovable-uploads/4a75c9be-a35f-43a4-99d5-e02066cae771.png" 
                    alt="Bilanga Logo" 
                    className="w-10 h-10 object-contain object-center"
                    style={{ imageRendering: 'auto', transform: 'scale(1)' }}
                    width="40"
                    height="40"
                    sizes="40px"
                    loading="eager"
                    decoding="sync"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const parent = target.parentElement?.parentElement;
                      console.log("Error loading logo in Header, using fallback");
                      // If error, replace with a letter (secure approach)
                      if (parent) {
                        target.style.display = 'none';
                        parent.textContent = 'B';
                        parent.classList.add('flex', 'items-center', 'justify-center', 'text-farm-dark-green', 'dark:text-farm-light-green', 'text-lg', 'font-bold');
                      }
                    }}
                  />
                </picture>
              </div>
              <h1 className="text-xl font-bold text-farm-dark-green dark:text-farm-light-green">
                {title}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {action && <div>{action}</div>}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Avatar className="h-8 w-8 cursor-pointer">
                    <AvatarImage src={profile?.avatar_url || user.user_metadata?.avatar_url} />
                    <AvatarFallback>{getInitials()}</AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    <span>{user.email}</span>
                  </DropdownMenuItem>
                  <Link to="/profile">
                    <DropdownMenuItem className="flex items-center">
                      <Settings className="h-4 w-4 mr-2" />
                      <span>Profil</span>
                    </DropdownMenuItem>
                  </Link>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Informations
                  </DropdownMenuLabel>
                  
                  <Link to="/about">
                    <DropdownMenuItem className="flex items-center">
                      <Info className="h-4 w-4 mr-2" />
                      <span>À propos</span>
                    </DropdownMenuItem>
                  </Link>
                  
                  <Link to="/privacy">
                    <DropdownMenuItem className="flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      <span>Confidentialité</span>
                    </DropdownMenuItem>
                  </Link>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center text-red-500">
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Déconnexion</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" className="text-primary hover:underline text-sm">
                Connexion
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
